import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@rio-cloud/rio-uikit/Button';
import ButtonToolbar from '@rio-cloud/rio-uikit/ButtonToolbar';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';

import type { ApiUpdateStop } from '#api/model/types.ts';
import { LocationFormView } from '#components/order/CreateOrderDialog/LocationForm.tsx';
import type { Stop } from '#domain/Stop.ts';

import {
    type EditStopFormData,
    EditStopFormValidation,
    mapEditStopFormDataToStop,
    mapEditStopFormDataToUpdateStop,
    mapStopToEditStopFormData,
} from './EditStopFormState.ts';
import { mapStopTypeToAddressType } from './mapStopTypeToAddressType.ts';

type EditStopDialogProps = {
    stop?: Stop;
    isOpen: boolean;
    isLoading?: boolean;
    onClose: () => void;
    onSubmit: (stopUpdate: ApiUpdateStop, updatedStop: Stop) => Promise<void> | void;
    validateStop?: (updatedStop: Stop) => { field: 'startDate' | 'endDate'; message: string } | undefined;
};

const validCoordinates = (latitude: number | undefined, longitude: number | undefined) =>
    latitude !== undefined &&
    longitude !== undefined &&
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    Math.abs(latitude) <= 90 &&
    Math.abs(longitude) <= 180;

export const EditStopDialog = ({
    stop,
    isOpen,
    isLoading = false,
    onClose,
    onSubmit,
    validateStop,
}: EditStopDialogProps) => {
    const intl = useIntl();
    const formContext = useForm<EditStopFormData>({
        resolver: zodResolver(EditStopFormValidation),
        mode: 'onTouched',
        values: stop ? mapStopToEditStopFormData(stop) : undefined,
    });
    const {
        handleSubmit,
        setError,
        clearErrors,
        setValue,
        formState: { errors },
    } = formContext;
    const [latitude, longitude] = useWatch({ control: formContext.control, name: ['latitude', 'longitude'] });

    useEffect(() => {
        if (validCoordinates(latitude, longitude)) {
            clearErrors('latitude');
        }
    }, [clearErrors, latitude, longitude]);

    const addressType = mapStopTypeToAddressType(stop?.type);
    const onAddressSuggestionSelected = (selectedLocation: EditStopFormData['location']) => {
        if (validCoordinates(selectedLocation.latitude, selectedLocation.longitude)) {
            setValue('latitude', selectedLocation.latitude);
            setValue('longitude', selectedLocation.longitude);
        } else {
            setValue('latitude', undefined);
            setValue('longitude', undefined);
            setValue('location.latitude', undefined);
            setValue('location.longitude', undefined);
        }
    };

    const submit = handleSubmit(async formValues => {
        if (!stop) {
            return;
        }
        const updatedStop = mapEditStopFormDataToStop(formValues, stop);
        const addressChanged = (['street', 'city', 'zipCode', 'country'] as const).some(
            field => stop.location[field] !== updatedStop.location[field]
        );
        if (addressChanged && !validCoordinates(formValues.latitude, formValues.longitude)) {
            setError('latitude', {
                type: 'manual',
                message: intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.route-preview.no-coordinates',
                    defaultMessage: 'No selected stops have valid coordinates.',
                }),
            });
            return;
        }
        const validationError = validateStop?.(updatedStop);

        if (validationError) {
            setError(validationError.field, { type: 'manual', message: validationError.message });
            return;
        }

        await onSubmit(mapEditStopFormDataToUpdateStop(formValues, stop), updatedStop);
        onClose();
    });

    return (
        <FormProvider {...formContext}>
            <Dialog
                show={isOpen}
                title={intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.tours.stop.edit.title',
                    defaultMessage: 'Edit stop',
                })}
                body={
                    <>
                        <LocationFormView
                            addressDisabled
                            addressType={addressType}
                            allowPastDates
                            commentName='comment'
                            onAddressSuggestionSelected={onAddressSuggestionSelected}
                            showCommentInitially={Boolean(stop?.comment)}
                        />
                        {errors.latitude?.message && <p role='alert'>{errors.latitude.message}</p>}
                    </>
                }
                footer={
                    <ButtonToolbar alignment='right'>
                        <Button bsStyle='default' onClick={onClose}>
                            {intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.common.cancel',
                                defaultMessage: 'Cancel',
                            })}
                        </Button>
                        <Button bsStyle='primary' onClick={() => submit()} disabled={isLoading}>
                            {intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.orders.edit-dialog.save',
                                defaultMessage: 'Save changes',
                            })}
                        </Button>
                    </ButtonToolbar>
                }
                onClose={onClose}
                bsSize='md'
                showCloseButton
            />
        </FormProvider>
    );
};
